'use client';

import dayjs from 'dayjs';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { FC, useMemo } from 'react';
import { parseSocialLink, SOCIAL_ICON_SRC } from '@/lib/components/EventCard/AmbassadorEventCard';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { formatEndDate } from '@/lib/utils/formatEndDate';

interface EventPageHeaderMetaInfo {
  endDate?: string;
  usersCount?: string | number;
  volume?: string | number;
  newsCount?: string | number;
  ambassadorSocialLink?: string;
}

const metaTextCls =
  'text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary whitespace-nowrap';

export const EventPageHeaderMetaInfo: FC<EventPageHeaderMetaInfo> = ({
  endDate,
  usersCount = 0,
  volume = 0,
  newsCount = 0,
  ambassadorSocialLink,
}) => {
  const t = useTranslations('common');
  const isEnded = endDate ? dayjs(endDate).isBefore(dayjs()) : false;

  const ambassador = useMemo(() => {
    if (!ambassadorSocialLink) return null;
    return parseSocialLink(ambassadorSocialLink);
  }, [ambassadorSocialLink]);

  return (
    <div className="flex flex-wrap items-center gap-4">
      {ambassador && (
        <a
          href={ambassadorSocialLink}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex shrink-0 items-center gap-1 transition-opacity hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
        >
          <Image src={SOCIAL_ICON_SRC[ambassador.platform]} alt="" width={16} height={16} />
          <span className={metaTextCls}>By @{ambassador.username}</span>
        </a>
      )}

      {endDate && (
        <div className="flex items-center gap-1">
          <Icon
            name="AlarmClock"
            size={20}
            className={cn('shrink-0', isEnded ? 'text-v2-text-primary' : 'text-v2-icon-primary')}
          />
          <span className={cn(metaTextCls, isEnded && 'text-v2-text-primary')}>
            {isEnded
              ? `${t('endIn.ended')} ${formatEndDate(endDate)}`
              : `${t('end')} ${formatEndDate(endDate)}`}
          </span>
        </div>
      )}

      {Number(usersCount) > 0 && (
        <div className="flex items-center gap-1">
          <Icon name="UserSingle" size={20} className="shrink-0 text-v2-icon-primary" />
          <span className={metaTextCls}>{usersCount}</span>
        </div>
      )}

      {Number(volume) > 0 && (
        <span className={metaTextCls}>{t('volume.short', { value: volume })}</span>
      )}

      <span className={metaTextCls}>{t('news', { value: newsCount })}</span>
    </div>
  );
};
